import TransportOrderRoutePointsSection from './TransportOrderRoutePointsSection';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMoneyPl } from '../utils/currency';
import { formatLocalizedNumber, formatLocalizedPercent } from '../utils/numberFormatting';
import { useFeedback } from '../utils/feedback';
import {
  Alert,
  Button,
  Form,
  Input,
  Space,
  Tag,
} from 'antd';
import { useCustomerOptions } from '../modules/reference-data/queries/useCustomerOptions';
import { useTransportCompanyOptions } from '../modules/transport-companies/queries/useTransportCompanyOptions';
import type { ColumnsType } from 'antd/es/table';
import {
  CloseOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderOptionDto } from '../dto/order';
import type { SupplierOrderDto } from '../dto/supplierOrder';
import type { TransportRouteDetailDto } from '../dto/transport';
import { fetchOrderTransports } from '../modules/orders/api/orderTransportApi';
import TransportOrdersCargoSection from './TransportOrdersCargoSection';
import {
  TransportFormSectionsProvider,
  useTransportFormSections,
} from './TransportFormSection';
import TransportSupplierOrdersField, { TransportSelectedSupplierOrdersList } from './TransportSupplierOrdersField';
import TransportRouteSection from './TransportRouteSection';
import TransportCarrierSection from './TransportCarrierSection';
import TransportCostsSection from './TransportCostsSection';
import TransportVehicleNotesSection from './TransportVehicleNotesSection';
import TransportStickySummary, {
  TRANSPORT_STICKY_OFFSET_BOTTOM,
  TRANSPORT_STICKY_SCROLL_CLEARANCE,
} from './TransportStickySummary';
import TransportDeliveryQuantityFields, {
  buildTransportDeliveryFormItems,
} from '../modules/transports/components/TransportDeliveryQuantityFields';
import {
  focusTransportRequiredField,
  TRANSPORT_ORDER_SELECTION_ANCHOR_ID,
  sectionForTransportRequiredField,
  useTransportRequiredFields,
} from './transportRequiredFields';
import {
  buildCargoKey,
  buildCargoQuantitySnapshotFields,
  buildCargoRows,
  buildCargoRowsMetricsKey,
  getTransportOrderProductDisplayName,
  type CargoItemFormValue,
} from './sharedTransportFormRows';
import {
  type AllocationHintMap,
  type AllocationMethod,
  buildAllocationPreview,
  buildSharedTransportFormValues,
  buildSharedTransportPayload,
  calculateCargoItemsMetrics,
  computeAllocationHints,
  DEFAULT_TRANSPORT_CURRENCY,
  deriveCustomerFromOrders,
  deriveDeliveryAddressesFromOrders,
  deriveLoadingAddressesFromOrders,
  deriveLdmCapacityIndicator,
  deriveWeightCapacityIndicator,
  getEffectiveTransportCargoItems,
  hasMissingCargoDimensions,
  hasMixedCargoQuantityUnits,
  ldmBasisMetricLabelKey,
  shouldPreventEnterFormSubmit,
  suggestItemAllocationMethodForBetweenOrders,
  type SharedTransportFormValues,
  type SharedTransportPreviewRow,
} from './sharedTransportFormUtils';
import { formatCargoQuantityTotals } from '../utils/cargoQuantity';
import {
  calculateTransportCostAllocation,
  commercialAllocationWarningKey,
  type AllocationMethod as CostAllocationMethod,
  type TransportCostAllocationWarning,
} from '../utils/transportCostAllocation';

interface SharedTransportFormProps {
  availableOrders: OrderOptionDto[];
  /**
   * Etap 2 (transport przywozowy): supplier orders (ZD) available for the
   * inbound link. Omitted (or empty) hides nothing — the field still renders so
   * the operator can see the transport carries no inbound leg — but a page that
   * never loads them simply passes an empty list.
   */
  availableSupplierOrders?: SupplierOrderDto[];
  supplierOrdersLoading?: boolean;
  supplierOrdersLoadError?: boolean;
  onRetrySupplierOrders?: () => void;
  transport?: TransportRouteDetailDto | null;
  initialOrderIds?: string[];
  /**
   * Punkt startu tworzenia z kontekstu zamówienia (decyzja 2026-09-14):
   * wstępnie wybiera to zamówienie i pokazuje kartę „Transport dla
   * zamówienia" jako informację — NIE ogranicza formularza. Operator może
   * dołożyć kolejne zamówienia (picker klientów zawsze pełny), a guard ZD
   * wymaga powiązania z co najmniej jednym wybranym zamówieniem.
   */
  orderScopeId?: string;
  canEditTransportCosts?: boolean;
  canViewTransportCosts?: boolean;
  loading?: boolean;
  onCancel?: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}

const SharedTransportFormInner: React.FC<SharedTransportFormProps> = ({
  availableOrders,
  availableSupplierOrders = [],
  supplierOrdersLoading = false,
  supplierOrdersLoadError = false,
  onRetrySupplierOrders,
  transport,
  initialOrderIds,
  orderScopeId,
  canEditTransportCosts = true,
  canViewTransportCosts = true,
  loading = false,
  onCancel,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation(['transport', 'common', 'orders', 'products']);
  const feedback = useFeedback();
  const [form] = Form.useForm<SharedTransportFormValues>();
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  // Etap 2 (transport przywozowy): supplier orders (ZD) carried inbound. Kept
  // in component state next to selectedOrderIds — the two are peers feeding the
  // same payload builder and the same "at least one link" invariant.
  const [selectedSupplierOrderIds, setSelectedSupplierOrderIds] = useState<string[]>([]);
  const [allocationHints, setAllocationHints] = useState<AllocationHintMap>({});
  const [allocationHintsLoading, setAllocationHintsLoading] = useState(false);
  const [allocationHintsError, setAllocationHintsError] = useState(false);
  // Prefill/auto-fill guards: once the operator edits customer, loading
  // addresses, or delivery addresses, we stop overwriting their input from the
  // selected orders.
  const [customerTouched, setCustomerTouched] = useState(false);
  const [loadingTouched, setLoadingTouched] = useState(false);
  const [deliveryTouched, setDeliveryTouched] = useState(false);
  const [quantityTouchedKeys, setQuantityTouchedKeys] = useState<Set<string>>(new Set());
  const [excludedCargoKeys, setExcludedCargoKeys] = useState<Set<string>>(new Set());
  // F5 (soft mirroring): once the operator explicitly picks an item-level
  // split method themselves, stop suggest-syncing it from the between-orders
  // key — same touched-guard precedent as customerTouched
  // above. Reset on transport switch (the effect below re-seeds it on load).
  const [costAllocationMethodTouched, setCostAllocationMethodTouched] = useState(false);
  // The historical between-orders default is LDM.  It is not an operator
  // choice, and must not turn the safe item-level default (commercial
  // quantity) into package-based LDM before packaging exists.
  const [allocationMethodTouched, setAllocationMethodTouched] = useState(false);
  const [showUnitCosts, setShowUnitCosts] = useState(false);
  const [stickySummaryHeight, setStickySummaryHeight] = useState(0);
  const { options: customerOptions } = useCustomerOptions();
  const { options: transportCompanyOptions } = useTransportCompanyOptions();

  const isEditMode = Boolean(transport);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(buildSharedTransportFormValues(transport));
    const nextSelectedIds = Array.from(new Set([
      ...(transport?.routeOrders.map((routeOrder) => routeOrder.orderId) || []),
      ...(initialOrderIds || []),
      // Decyzja 2026-09-14: tworzenie z kontekstu zamówienia wstępnie wybiera
      // (nie ogranicza). orderScopeId dokłada się do selekcji jak initialOrderIds,
      // więc operator może dołożyć kolejne zamówienia do tej ciężarówki.
      ...(orderScopeId ? [orderScopeId] : []),
    ]));
    setSelectedOrderIds(nextSelectedIds);
    // Etap 2: seeded from the loaded transport only — there is no
    // "?supplierOrderId=" create-mode preset yet, so create mode starts empty.
    setSelectedSupplierOrderIds(
      (transport?.supplierOrders || []).map((supplierOrder) => supplierOrder.id),
    );
    setExcludedCargoKeys(new Set());
    setAllocationHints({});
    setAllocationHintsLoading(false);
    setAllocationHintsError(false);
    setQuantityTouchedKeys(new Set());
    // Edit mode loads stored values, so all fields count as user-owned.
    setCustomerTouched(Boolean(transport));
    setLoadingTouched(Boolean(transport));
    setDeliveryTouched(Boolean(transport));
    // F5 (soft mirroring): a loaded transport's item-level method is the
    // operator's earlier, deliberate choice — never silently overwrite it.
    setCostAllocationMethodTouched(Boolean(transport));
    // A stored route allocation is likewise a prior explicit decision. In
    // create mode, the LDM default is merely a compatibility default and must
    // not trigger item-level LDM allocation.
    setAllocationMethodTouched(Boolean(transport));
    setShowUnitCosts(false);
  }, [form, transport, initialOrderIds, orderScopeId]);

  // Fetch "already allocated elsewhere" per selected order so the cargo editor
  // can show remaining quantities. The subtraction of quantities already on the
  // current transport (when editing) is encapsulated in `computeAllocationHints`
  // so the invariant lives in one place.
  useEffect(() => {
    if (selectedOrderIds.length === 0) {
      setAllocationHints({});
      setAllocationHintsLoading(false);
      setAllocationHintsError(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setAllocationHintsLoading(true);
      setAllocationHintsError(false);
      const results = await Promise.all(
        selectedOrderIds.map(async (orderId) => {
          try {
            return { data: await fetchOrderTransports(orderId), failed: false };
          } catch (error) {
            return {
              data: { transports: [], productRealization: [] },
              failed: true,
            };
          }
        }),
      );

      if (cancelled) return;
      setAllocationHints(computeAllocationHints(results.map((result) => result.data), transport));
      setAllocationHintsError(results.some((result) => result.failed));
      setAllocationHintsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedOrderIds, transport]);

  const selectedOrders = availableOrders.filter((order) => selectedOrderIds.includes(order.id));
  const scopedOrder = orderScopeId
    ? availableOrders.find((order) => order.id === orderScopeId)
    : undefined;
  const cargoItemsByKey = (Form.useWatch('cargoItemsByKey', form) || {}) as Record<string, CargoItemFormValue>;
  const currency = Form.useWatch('currency', form) as string | undefined;
  const status = Form.useWatch('status', form) as string | undefined;
  const effectiveTransportCargoItems = getEffectiveTransportCargoItems(transport);
  const allCargoRows = buildCargoRows(
    selectedOrders,
    cargoItemsByKey,
    allocationHints,
    effectiveTransportCargoItems,
    t('products:catalogPicker.basicProduct'),
  );
  const cargoRows = allCargoRows.filter((row) => !excludedCargoKeys.has(row.key));
  const missingRequiredFields = useTransportRequiredFields({
    form,
    selectedOrderIds,
    // Etap 2: an inbound-only transport satisfies the "orders" requirement
    // through its supplier orders, so the checklist must see both sides.
    selectedSupplierOrderIds,
    cargoRows,
    canEditTransportCosts,
  });
  // Sections are collapsed by default: expand the owning one first, then
  // focus once the panel has had a frame to lay out.
  const sections = useTransportFormSections();
  const focusRequiredField = useCallback(
    (field: Parameters<typeof focusTransportRequiredField>[1]) => {
      sections?.openSection(sectionForTransportRequiredField(field));
      window.setTimeout(() => focusTransportRequiredField(form, field), sections ? 250 : 0);
    },
    [form, sections],
  );
  const selectedCargoItems = cargoRows
    .filter((row) => Number(row.quantity || 0) > 0)
    .map(({
      key,
      orderNumber,
      availableQuantity,
      allocatedElsewhere,
      productDimensions,
      productVariantLabels,
      ...item
    }) => item);
  const deliveryCargoKey = JSON.stringify(selectedCargoItems.map((item) => ({
    orderProductId: item.orderProductId,
    quantity: item.quantity,
    unit: item.unit,
    quantityPcs: item.quantityPcs,
    quantityM2: item.quantityM2,
    quantityM3: item.quantityM3,
  })));

  useEffect(() => {
    if (status !== 'DELIVERED') {
      form.setFieldValue('deliveryItems', []);
      return;
    }

    const currentDeliveryItems = (form.getFieldValue('deliveryItems') || []) as Array<{
      orderProductId?: string;
      deliveredQuantity?: number;
    }>;
    const currentByProductId = new Map(currentDeliveryItems.map((item) => [
      item.orderProductId,
      item.deliveredQuantity,
    ]));
    const nextItems = buildTransportDeliveryFormItems(selectedCargoItems).map((item) => ({
      ...item,
      deliveredQuantity: currentByProductId.has(item.orderProductId)
        ? currentByProductId.get(item.orderProductId)
        : item.deliveredQuantity,
    }));
    form.setFieldValue('deliveryItems', nextItems);
    // selectedCargoItems is represented by the stable identity/quantity key.
    // Re-running for a cost-only render would overwrite in-progress delivery
    // confirmation input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryCargoKey, form, status]);
  const orderIdsWithCargo = new Set(selectedCargoItems.map((item) => item.orderId));
  const missingCargoOrderNumbers = selectedOrders
    .filter((order) => !orderIdsWithCargo.has(order.id))
    .map((order) => order.displayOrderNumber || order.orderNumber);
  const hasMissingCustomerCargo = missingCargoOrderNumbers.length > 0;
  const allocationMethod = Form.useWatch('allocationMethod', form) as AllocationMethod | undefined;
  const costAllocationMethod = (
    Form.useWatch('costAllocationMethod', form) as CostAllocationMethod | undefined
  ) || 'BY_COMMERCIAL_QTY';
  const transportRate = Form.useWatch('transportRate', form) as number | null | undefined;
  const multiOrder = selectedOrders.length >= 2;
  const transportCostForAllocation = canEditTransportCosts
    ? Number(transportRate || 0)
    : Number(transport?.transportRate ?? transport?.transportCost ?? 0);
  // Rows with quantity 0 mean "not loaded on this transport" and are excluded
  // from the payload, so they must not participate in the cost allocation.
  const transportCostAllocation = calculateTransportCostAllocation({
    transportCost: transportCostForAllocation,
    method: costAllocationMethod,
    items: cargoRows
      .filter((row) => Number(row.quantity || 0) > 0)
      .map((row) => ({
        id: row.id || row.key,
        quantity: row.quantity,
        unit: row.unit,
        goodsValueTotal: row.goodsValueTotal,
        manualAllocatedTransportCost: row.manualAllocatedTransportCost,
        // Product identity + dimensions (mm) feed the BY_SQM / BY_CBM / BY_LDM
        // metric methods (calculateAllocationMetric → normalizeItem). Cargo rows
        // already carry these from the order products; without them the metric
        // methods would always raise MISSING_DIMENSIONS in the live preview.
        // These are display-only inputs to the client-side allocation; the saved
        // payload is unchanged (the backend re-derives dims from stored
        // TransportCargoItem rows).
        name: row.productName,
        width: row.width,
        height: row.height,
        length: row.length,
        thickness: row.thickness,
        // Packaging feeds BY_LDM's package-based footprint in the live preview
        // (same as the saved snapshot). Without it BY_LDM would always raise
        // MISSING_PACKAGING in the preview.
        piecesPerPackage: row.piecesPerPackage,
        packageLength: row.packageLength,
        packageWidth: row.packageWidth,
        packageStackLayers: row.packageStackLayers,
        // F2: a positive manual LDM override wins over packaging in the live
        // preview too (same precedence as the saved snapshot/resolveItemLdm).
        manualLdm: row.manualLdm,
      })),
  });
  const allocationItemsById = new Map(
    transportCostAllocation.items.map((item) => [item.id, item]),
  );
  // Display-only preview: split the rate as entered, in the form's own
  // currency. No FX conversion — what is SAVED to the backend is untouched;
  // this only feeds the "Podgląd alokacji kosztu" table below.
  const previewTransportRate = canEditTransportCosts ? Number(transportRate || 0) : 0;
  const { rows: allocationPreview, effectiveLdmMetric, fallbackMetric, missingDimensionOrders } = buildAllocationPreview(
    selectedOrders,
    previewTransportRate,
    allocationMethod || 'LDM',
    selectedCargoItems
  );
  const hasMixedQuantityUnits =
    allocationMethod === 'QUANTITY' && hasMixedCargoQuantityUnits(selectedCargoItems);

  // F5 (soft mirroring): when the operator changes the between-orders split
  // key AND the item-level method is still untouched by them this session,
  // suggest-sync the item-level method to the same family (LDM->BY_LDM,
  // CBM->BY_CBM, SQM->BY_SQM, QUANTITY->BY_COMMERCIAL_QTY). Never runs once
  // costAllocationMethodTouched is true — a user-picked value (or a loaded
  // transport's stored value, which counts as touched on load) is never
  // silently overwritten. WEIGHT has no item-level equivalent and suggests
  // nothing (suggestItemAllocationMethodForBetweenOrders returns undefined).
  useEffect(() => {
    if (costAllocationMethodTouched || !allocationMethodTouched || !allocationMethod) {
      return;
    }
    const suggestion = suggestItemAllocationMethodForBetweenOrders(allocationMethod);
    if (suggestion && form.getFieldValue('costAllocationMethod') !== suggestion) {
      form.setFieldValue('costAllocationMethod', suggestion);
    }
  }, [allocationMethod, allocationMethodTouched, costAllocationMethodTouched, form]);

  // Create-mode auto-fill: keep customer in sync with the selected orders until
  // the operator types their own value. Clearing the selection resets it.
  useEffect(() => {
    if (isEditMode || customerTouched) {
      return;
    }

    form.setFieldValue('customer', deriveCustomerFromOrders(selectedOrders));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds, isEditMode, customerTouched, availableOrders]);

  // Create-mode prefill: fill the loading-address blocks from the selected
  // orders' factory places until the operator edits the list. When no order
  // carries an address we leave the single empty block (same as before).
  useEffect(() => {
    if (isEditMode || loadingTouched) {
      return;
    }

    const prefilled = deriveLoadingAddressesFromOrders(selectedOrders);
    form.setFieldValue('loadingAddresses', prefilled.length > 0 ? prefilled : ['']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds, isEditMode, loadingTouched, availableOrders]);

  // Create-mode prefill: fill the delivery-address blocks from the selected
  // orders' unload places until the operator edits the list. When no order
  // carries an address we leave the single empty block (same as before).
  useEffect(() => {
    if (isEditMode || deliveryTouched) {
      return;
    }

    const prefilled = deriveDeliveryAddressesFromOrders(selectedOrders);
    form.setFieldValue('deliveryAddresses', prefilled.length > 0 ? prefilled : ['']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds, isEditMode, deliveryTouched, availableOrders]);

  useEffect(() => {
    const previousItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
    const nextItems = { ...previousItems };
    let changed = false;
    const existingTransportOrderIds = new Set(
      transport?.routeOrders.map((routeOrder) => routeOrder.orderId) || []
    );
    const transportHasCargoItems = Boolean(transport && transport.cargoItems.length > 0);
    const currentTransportCargoItems = getEffectiveTransportCargoItems(transport);

    availableOrders
      .filter((order) => selectedOrderIds.includes(order.id))
      .forEach((order) => {
        order.products.forEach((product) => {
          const key = `${order.id}:${product.id}`;
          if (nextItems[key] !== undefined) {
            return;
          }

          const existingCargoItem = currentTransportCargoItems.find((item) =>
            item.orderId === order.id && item.orderProductId === product.id
          );
          const shouldDefaultMissingCargoToZero =
            transportHasCargoItems && existingTransportOrderIds.has(order.id);
          const quantitySnapshotFields = buildCargoQuantitySnapshotFields(
            product,
            existingCargoItem,
          );

          nextItems[key] = {
            id: existingCargoItem?.id,
            quantity: existingCargoItem?.quantity
              ?? (shouldDefaultMissingCargoToZero ? 0 : Math.max(Number(product.quantity || 0), 0)),
            unit: (existingCargoItem?.unit ?? product.unit ?? 'pcs') as CargoItemFormValue['unit'],
            ...quantitySnapshotFields,
            goodsValueTotal: existingCargoItem?.goodsValueTotal ?? 0,
            manualAllocatedTransportCost: existingCargoItem?.manualAllocatedTransportCost ?? null,
            // Package-based LDM: seed the actual Form.Item values (not just the
            // buildCargoRows display fallback) so the per-line packaging popover
            // shows the line's real packaging instead of blank inputs (LOW-4).
            // Same source priority as buildCargoRows: persisted cargo-item
            // snapshot (edit mode) first, then the product catalog default.
            piecesPerPackage: existingCargoItem?.piecesPerPackage ?? product.piecesPerPackage ?? null,
            packageLength: existingCargoItem?.packageLength ?? product.packageLength ?? null,
            packageWidth: existingCargoItem?.packageWidth ?? product.packageWidth ?? null,
            packageStackLayers: existingCargoItem?.packageStackLayers ?? product.packageStackLayers ?? null,
            // F2: seed the manual LDM override the same way (LOW-4 precedent) —
            // no product-level default exists, so it falls back to the cargo
            // snapshot only.
            manualLdm: existingCargoItem?.manualLdm ?? null,
          };
          changed = true;
        });
      });

    if (changed) {
      form.setFieldsValue({ cargoItemsByKey: nextItems });
    }
  }, [availableOrders, form, selectedOrderIds, transport]);

  // Create mode starts each untouched line at the quantity that is genuinely
  // still available after other active transports. The realization request is
  // asynchronous, so this second pass corrects rows that were seeded before
  // the response arrived. A quantity the operator has already touched is
  // never overwritten.
  useEffect(() => {
    if (isEditMode || allocationHintsLoading || allocationHintsError) {
      return;
    }

    const previousItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
    const nextItems = { ...previousItems };
    let changed = false;

    selectedOrders.forEach((order) => {
      order.products.forEach((product) => {
        const key = buildCargoKey(order.id, product.id);
        if (quantityTouchedKeys.has(key) || excludedCargoKeys.has(key) || !previousItems[key]) {
          return;
        }

        const hint = allocationHints[product.id];
        const remainingQuantity = hint
          ? Math.max(Number(hint.remainingQuantity || 0), 0)
          : Math.max(Number(product.quantity || 0), 0);
        if (Number(previousItems[key].quantity || 0) === remainingQuantity) {
          return;
        }

        nextItems[key] = { ...previousItems[key], quantity: remainingQuantity };
        changed = true;
      });
    });

    if (changed) {
      form.setFieldsValue({ cargoItemsByKey: nextItems });
    }
  }, [
    allocationHints,
    allocationHintsError,
    allocationHintsLoading,
    excludedCargoKeys,
    form,
    isEditMode,
    quantityTouchedKeys,
    selectedOrders,
  ]);

  const markQuantityTouched = useCallback((key: string) => {
    setQuantityTouchedKeys((previous) => {
      if (previous.has(key)) return previous;
      const next = new Set(previous);
      next.add(key);
      return next;
    });
  }, []);

  // F5 (soft mirroring): mark the item-level split method as operator-owned
  // the moment they change it directly, so the between-orders-key mirroring
  // effect above stops suggesting a value for it.
  const markCostAllocationMethodTouched = useCallback(() => {
    setCostAllocationMethodTouched(true);
  }, []);

  const markAllocationMethodTouched = useCallback(() => {
    setAllocationMethodTouched(true);
  }, []);

  const updateStickySummaryHeight = useCallback((height: number) => {
    setStickySummaryHeight((currentHeight) =>
      currentHeight === height ? currentHeight : height
    );
  }, []);

  // Mirrors backend transportService.formatSplitTruckDimensions: area/volume are
  // omitted when zero, and package-based LDM renders "—" when it cannot be
  // computed (no packaging on any line → ldm 0) OR is incomplete (≥1 loaded line
  // missing packaging), e.g. "750.000 m² / 57.000 m³ / — LDM".
  const formatMetricsSummary = (metrics: ReturnType<typeof calculateCargoItemsMetrics>): string => {
    const ldmText = metrics.ldmIncomplete || !metrics.ldm
      ? '—'
      : formatLocalizedNumber(metrics.ldm, 2, i18n.language);
    return [
      metrics.areaSqm ? `${formatLocalizedNumber(metrics.areaSqm, 3, i18n.language)} m²` : null,
      metrics.volume ? `${formatLocalizedNumber(metrics.volume, 3, i18n.language)} m³` : null,
      `${ldmText} LDM`,
    ]
      .filter(Boolean)
      .join(' / ');
  };

  // Cargo totals are derived values: auto-recalculated whenever the loaded
  // quantities, packaging, or the selected order set change, and never edited
  // by hand. The row key + metrics-input snapshot (buildCargoRowsMetricsKey)
  // keeps this effect from re-running on every render when selectedCargoItems
  // is freshly derived above, while still catching every input the LDM/m²/m³
  // summary depends on — including a packaging-only edit via the per-line
  // popover, which does not change quantity (browser-regression MED-3: a key
  // that only tracked quantity let this effect skip re-running and the form
  // kept submitting the PRE-EDIT "— LDM" dimensions string).
  const cargoRowsKey = useMemo(() => buildCargoRowsMetricsKey(cargoRows), [cargoRows]);
  const selectedOrdersKey = useMemo(() => selectedOrderIds.join(','), [selectedOrderIds]);

  useEffect(() => {
    const metrics = calculateCargoItemsMetrics(selectedCargoItems);
    form.setFieldsValue({
      packages: metrics.packages,
      totalWeight: metrics.weight,
      totalVolume: metrics.volume,
      dimensions: selectedCargoItems.length > 0 ? formatMetricsSummary(metrics) : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargoRowsKey, selectedOrdersKey]);

  const hasMissingDimensions = hasMissingCargoDimensions(selectedCargoItems);

  const handleFinish = async (values: SharedTransportFormValues) => {
    // Etap 2: the invariant is "at least one link, customer OR supplier order"
    // — an inbound-only truck ("zamówili sobie na magazyn") is valid, and so is
    // unlinking every supplier order as long as a customer order remains. This
    // mirrors the backend's TRANSPORT_HAS_NO_ORDERS guard, so the only case
    // stopped here is the one the backend would refuse anyway.
    if (selectedOrderIds.length === 0 && selectedSupplierOrderIds.length === 0) {
      feedback.warning(t('transport:supplierOrders.selectAtLeastOneOrderOrSupplierOrder'), { sticky: true });
      focusRequiredField({ id: 'order', kind: 'order' });
      return;
    }

    if (allocationHintsLoading) {
      feedback.warning(t('transport:cargo.availabilityLoading'), { sticky: true });
      return;
    }

    if (allocationHintsError) {
      feedback.error(t('transport:cargo.availabilityLoadError'), { sticky: true });
      return;
    }

    if (hasMissingCustomerCargo) {
      feedback.error(t('transport:cargo.orderWithoutCargoBlocked', {
        numbers: missingCargoOrderNumbers.join(', '),
      }), { sticky: true });
      return;
    }

    if (hasMixedQuantityUnits) {
      feedback.error(t('transport:allocation.mixedQuantityUnitsBlocked'), { sticky: true });
      return;
    }

    await form.validateFields([['cargoItemsByKey']], { recursive: true });

    const payload = buildSharedTransportPayload(
      values,
      selectedOrderIds,
      selectedCargoItems,
      selectedSupplierOrderIds,
    );
    if (!canEditTransportCosts) {
      delete payload.transportRate;
      delete payload.currency;
      delete payload.invoiceNumber;
    }

    await onSubmit(payload);
  };

  const handleSupplierOrderChange = useCallback((nextSupplierOrderIds: string[]) => {
    setSelectedSupplierOrderIds(Array.from(new Set(nextSupplierOrderIds.filter(Boolean))));
  }, []);

  const availableColumns: ColumnsType<OrderOptionDto> = [
    {
      title: t('transport:orderNumber'),
      key: 'displayOrderNumber',
      onCell: () => ({
        className: 'transport-order-picker-cell',
        'data-label': t('transport:orderNumber'),
      }),
      render: (_, record) => record.displayOrderNumber || record.orderNumber,
    },
    {
      title: t('transport:customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      onCell: () => ({
        className: 'transport-order-picker-cell',
        'data-label': t('transport:customer'),
      }),
    },
    {
      title: t('transport:table.products'),
      key: 'products',
      onCell: () => ({
        className: 'transport-order-picker-cell',
        'data-label': t('transport:table.products'),
      }),
      render: (_, record) => record.products
        .map(getTransportOrderProductDisplayName)
        .join(', ') || '-',
    },
    {
      title: t('transport:statusColumn'),
      dataIndex: 'status',
      key: 'status',
      onCell: () => ({
        className: 'transport-order-picker-cell',
        'data-label': t('transport:statusColumn'),
      }),
      // The `availableOrders` rows carry ORDER statuses (NEW_INQUIRY,
      // QUOTE_SENT, ...), not TRANSPORT statuses (PENDING, SCHEDULED, ...).
      // Look up the translation in the orders namespace; fall back to a
      // a neutral operator-facing label if a new status lands before its
      // translation. Never expose the backend enum in Polish UI.
      render: (value) => (
        <Tag>
          {t(`orders:statusOptions.${value}`, {
            defaultValue: t('common:unknownStatus'),
          })}
        </Tag>
      ),
    },
  ];

  const previewColumns: ColumnsType<SharedTransportPreviewRow> = [
    {
      title: t('transport:orderNumber'),
      dataIndex: 'displayOrderNumber',
      key: 'displayOrderNumber',
    },
    {
      title: t('transport:customer'),
      dataIndex: 'customerName',
      key: 'customerName',
    },
    {
      title: t('transport:packages'),
      key: 'packages',
      render: (_, record) => formatCargoQuantityTotals(record.metrics.quantityByUnit, i18n.language),
    },
    {
      title: t('transport:weight'),
      key: 'weight',
      render: (_, record) => `${formatLocalizedNumber(record.metrics.weight, 2, i18n.language)} kg`,
    },
    {
      title: 'LDM',
      key: 'ldm',
      // Package-based LDM: "—" when incomplete or zero (no packaging data).
      render: (_, record) => (record.metrics.ldmIncomplete || !record.metrics.ldm
        ? '—'
        : formatLocalizedNumber(record.metrics.ldm, 2, i18n.language)),
    },
    {
      // INDIA (MED "no unit label"): show the EFFECTIVE metric the basis
      // column is denominated in, not a bare "Wartość bazowa". Under LDM this
      // may silently be a fallback metric (m³/m²/packages/kg) rather than
      // real LDM — see the fallbackMetric warning Alert in
      // TransportCostsSection for the accompanying explicit warning.
      title: effectiveLdmMetric !== undefined
        ? t('transport:allocation.basisWithMetric', {
          metric: t(`transport:${ldmBasisMetricLabelKey(effectiveLdmMetric ?? 'ldm')}`),
        })
        : t('transport:allocation.basis'),
      key: 'basisValue',
      render: (_, record) => formatLocalizedNumber(record.basisValue, 3, i18n.language),
    },
    {
      title: t('transport:allocation.percent'),
      key: 'allocationPercent',
      render: (_, record) => formatLocalizedPercent(record.allocationPercent, i18n.language, 2),
    },
    ...(canEditTransportCosts ? [{
      title: t('transport:allocation.cost'),
      key: 'allocatedTransportCost',
      render: (_: unknown, record: SharedTransportPreviewRow) => formatMoneyPl(Number(record.allocatedTransportCost || 0), currency || DEFAULT_TRANSPORT_CURRENCY),
    }] : []),
  ];

  // Polish-first money text (S36 follow-up, live-behavior observation
  // 2026-07-07): toFixed(2) rendered en-US decimals ("3400.00 PLN") in the
  // create-form footer/preview — route through the formatMoneyPl single home
  // instead; '\u2014' em dash for empty, unified with the detail page.
  const formatCurrencyAmount = (value: number | null | undefined): string =>
    value == null ? '\u2014' : formatMoneyPl(Number(value), currency || DEFAULT_TRANSPORT_CURRENCY);

  const renderAllocationWarning = (
    warning: TransportCostAllocationWarning,
    index: number,
  ) => {
    const difference = formatCurrencyAmount(transportCostAllocation.summary.allocationDifference);
    // Offending product names for MISSING_DIMENSIONS, so the operator knows which
    // cargo lines lack dimensions. Unnamed lines are dropped; if none remain we
    // fall back to the generic message with no name suffix.
    const missingDimensionNames =
      warning.code === 'MISSING_DIMENSIONS'
        ? (warning.items || [])
            .map((item) => (item.name || '').trim())
            .filter((name) => name.length > 0)
            .join(', ')
        : '';

    let description: React.ReactNode;
    if (warning.code === 'MANUAL_SUM_MISMATCH') {
      description = t('transport:commercialAllocationWarningDifference', { difference });
    } else if (warning.code === 'MISSING_DIMENSIONS' && missingDimensionNames) {
      description = t('transport:missingDimensionsItems', { names: missingDimensionNames });
    }

    return (
      <Alert
        key={`${warning.code}-${warning.itemId || index}`}
        type="warning"
        showIcon
        message={t(`transport:${commercialAllocationWarningKey(warning.code)}`)}
        description={description}
        style={{ marginTop: 8 }}
      />
    );
  };

  const orderRowSelection = {
    selectedRowKeys: selectedOrderIds,
    onChange: (keys: React.Key[]) => {
      const nextIds = keys.map((key) => String(key));
      setAllocationHintsLoading(nextIds.length > 0);
      setAllocationHintsError(false);
      setSelectedOrderIds(nextIds);
    },
  };

  // Duplicate-submit-button fix (coordinator triage, 2026-07-07): this form
  // used to render the SAME submit/cancel pair in TWO places — a top action
  // bar and TransportStickySummary's footer bar — parametrized by `location`
  // purely so their duplicate accessible names didn't collide in
  // `getByRole('button', { name: ... })`-style test lookups. The top copy is
  // now gone (the fixed TransportStickySummary is the ONLY submit surface and
  // remains visible throughout the scroll range). No more
  // location parameter needed since there is exactly one instance again, but
  // the `-sticky` suffix on the testid/aria-label is kept (rather than
  // reverted to a bare id) so existing `-sticky` references elsewhere do not
  // need to change; only `-top` lookups needed updating (see the `-top`
  // search below — the bare id was never in use).
  const stickySubmitButtons = (
    <Space wrap size={[8, 8]}>
      {onCancel ? (
        <Button
          onClick={onCancel}
          icon={<CloseOutlined />}
          data-testid="transport-form-cancel-sticky"
        >
          {t('common:cancel')}
        </Button>
      ) : null}
      <Button
        type="primary"
        htmlType="submit"
        loading={loading}
        disabled={
          hasMixedQuantityUnits
          || hasMissingCustomerCargo
          || allocationHintsLoading
          || allocationHintsError
        }
        icon={<SaveOutlined />}
        data-testid="transport-form-submit-sticky"
        aria-label={`${transport ? t('transport:updateRoute') : t('transport:createTransport')} (sticky)`}
      >
        {transport ? t('transport:updateRoute') : t('transport:createTransport')}
      </Button>
    </Space>
  );

  const cargoStats = calculateCargoItemsMetrics(selectedCargoItems);
  const cargoStatItems: Array<{ key: string; label: string; value: string }> = [
    { key: 'packages', label: t('transport:fields.packages'), value: String(cargoStats.packages) },
    { key: 'weight', label: t('transport:fields.totalWeight'), value: `${formatLocalizedNumber(cargoStats.weight, 2, i18n.language)} kg` },
    { key: 'volume', label: t('transport:fields.totalVolume'), value: `${formatLocalizedNumber(cargoStats.volume, 3, i18n.language)} m³` },
    {
      key: 'dimensions',
      label: t('transport:fields.dimensions'),
      value: selectedCargoItems.length > 0 ? formatMetricsSummary(cargoStats) : '—',
    },
  ];
  // F1 (trailer capacity indicator): display-only, never blocks save.
  const ldmCapacity = deriveLdmCapacityIndicator(cargoStats.ldm, cargoStats.ldmIncomplete);
  const weightCapacity = deriveWeightCapacityIndicator(cargoStats.weight);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onFinishFailed={({ errorFields }) => {
        feedback.warning(t('transport:messages.fillRequired'));
        if (missingRequiredFields[0]) {
          focusRequiredField(missingRequiredFields[0]);
        } else if (errorFields[0]) {
          // A rule-level error may sit in any collapsed panel: reveal them all.
          sections?.openAll();
          window.setTimeout(() => form.scrollToField(errorFields[0].name, {
            behavior: 'smooth',
            block: 'center',
            focus: true,
          }), sections ? 250 : 0);
        }
      }}
      autoComplete="off"
      // HIGH (Enter submit): without this, pressing Enter while e.g. typing
      // "Firma transportowa" bubbles to antd's
      // implicit form submit and CREATES the transport mid-configuration.
      // shouldPreventEnterFormSubmit allows Enter in the notes <textarea> and
      // on the real submit <button>, blocks it everywhere else.
      onKeyDown={(event) => {
        if (shouldPreventEnterFormSubmit(event)) {
          event.preventDefault();
        }
      }}
      // The summary is fixed above the viewport bottom, so the form must reserve
      // enough trailing space for the final fields to scroll clear of it.
      // Reserve the ACTUAL sticky-surface height reported by ResizeObserver,
      // plus its bottom offset and a small focus/scroll clearance. This stays
      // correct when mobile wrapping or a longer translation makes the footer
      // taller; no guessed fixed padding that can regress at another width.
      // the actual last field always has room to scroll fully clear of the
      // fixed strip before running out of page to scroll.
      style={{
        paddingBottom:
          stickySummaryHeight + TRANSPORT_STICKY_OFFSET_BOTTOM + TRANSPORT_STICKY_SCROLL_CLEARANCE,
        // Keep fields that live above the fixed summary scrollable into a
        // clear viewport area. This matters for the inbound ZD picker: a
        // browser click/focus must not land underneath the sticky actions.
        '--transport-form-sticky-clearance': `${
          stickySummaryHeight + TRANSPORT_STICKY_OFFSET_BOTTOM + TRANSPORT_STICKY_SCROLL_CLEARANCE
        }px`,
      } as React.CSSProperties & { '--transport-form-sticky-clearance'?: string }}
    >
      {/* S40 (coordinator triage, 2026-07-07): root-cause fix for the manualLdm
          (and every other cargoItemsByKey.*) hydration bug — a missing-
          Form.Item class of bug (a watched namePath with no registered Field
          entity anywhere along it silently never fires Form.useWatch).
          cargoItemsByKey is only ever registered per-LEAF-FIELD
          (cargoItemsByKey.<key>.manualLdm etc., inside
          TransportOrdersCargoSection's per-row Form.Items), never as the
          PARENT path itself — but this component watches the parent path
          directly (`Form.useWatch('cargoItemsByKey', form)` above) to drive
          buildCargoRows. On an edit-mode load, the seed useEffect below
          writes the persisted snapshot (including manualLdm) into
          cargoItemsByKey via form.setFieldValue BEFORE any per-row Form.Item
          exists yet (the rows haven't rendered — they depend on this same
          watched value), so rc-field-form has no registered Field entity to
          attach the watcher to and the write is silently invisible to
          Form.useWatch forever (confirmed in an isolated repro: the watched
          value stayed `{}` even after flushing effects). Net effect: every
          row falls through to the packaging-computed LDM (buildCargoRows'
          manualLdm: toFiniteOrNull(formItem.manualLdm) reads undefined ->
          null from the never-updated watch), matching the exact reported
          symptom — reload shows the computed 12.50 with the "ręcznie" tag
          gone despite the API returning manualLdm: 7.7. This hidden anchor
          Form.Item registers a Field entity for the parent path itself —
          always mounted, not conditional, so the watch is live from the very
          first render. */}
      <Form.Item name="cargoItemsByKey" hidden noStyle>
        <Input />
      </Form.Item>

      {/* Section order (operator feedback 2026-09): kto wiezie → co wiezie →
          dokąd → szczegóły. DOM order only; form state and effects are
          unchanged. */}
      {/* a. Przewoźnik */}
      <TransportCarrierSection
        form={form}
        customerOptions={customerOptions}
        transportCompanyOptions={transportCompanyOptions}
        onCustomerTouched={() => setCustomerTouched(true)}
      />

      {/* b. Trasa i terminy (+ trasa każdego zamówienia) */}
      <TransportRouteSection
        onLoadingTouched={() => setLoadingTouched(true)}
        onDeliveryTouched={() => setDeliveryTouched(true)}
        routePoints={<TransportOrderRoutePointsSection form={form} orders={selectedOrders} />}
      />

      {/* c. Zamówienia i ładunek — picker modal + selected orders + cargo items */}
      <div id={TRANSPORT_ORDER_SELECTION_ANCHOR_ID} tabIndex={-1}>
        <TransportOrdersCargoSection
          transport={transport}
          supplierOrdersField={(
            <TransportSupplierOrdersField
              availableSupplierOrders={availableSupplierOrders}
              selectedSupplierOrders={transport?.supplierOrders}
              value={selectedSupplierOrderIds}
              onChange={handleSupplierOrderChange}
              loading={supplierOrdersLoading}
              loadError={supplierOrdersLoadError}
              onRetry={onRetrySupplierOrders}
            />
          )}
          supplierOrdersSelectedList={(
            <TransportSelectedSupplierOrdersList
              availableSupplierOrders={availableSupplierOrders}
              selectedSupplierOrders={transport?.supplierOrders}
              value={selectedSupplierOrderIds}
              onChange={handleSupplierOrderChange}
            />
          )}
          selectedSupplierOrderCount={selectedSupplierOrderIds.length}
          canEditTransportCosts={canEditTransportCosts}
          availableOrders={availableOrders}
          availableColumns={availableColumns}
          orderRowSelection={orderRowSelection}
          scopedOrder={scopedOrder}
          orderScopeActive={Boolean(orderScopeId)}
          cargoRows={cargoRows}
          allocationHintsLoading={allocationHintsLoading}
          allocationHintsError={allocationHintsError}
          missingCargoOrderNumbers={missingCargoOrderNumbers}
          currency={currency}
          costAllocationMethod={costAllocationMethod}
          showUnitCosts={showUnitCosts}
          onShowUnitCostsChange={setShowUnitCosts}
          allocationItemsById={allocationItemsById}
          formatCurrencyAmount={formatCurrencyAmount}
          onQuantityTouched={markQuantityTouched}
          onRemoveCargo={(key) => {
            markQuantityTouched(key);
            const currentItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
            form.setFieldsValue({
              cargoItemsByKey: {
                ...currentItems,
                [key]: {
                  ...currentItems[key],
                  quantity: 0,
                },
              },
            });
            setExcludedCargoKeys((current) => new Set(current).add(key));
          }}
          cargoStatItems={cargoStatItems}
          ldmCapacity={ldmCapacity}
          weightCapacity={weightCapacity}
          hasMissingDimensions={hasMissingDimensions}
          deliveryQuantities={status === 'DELIVERED' && selectedCargoItems.length > 0 ? (
            <TransportDeliveryQuantityFields
              cargoItems={selectedCargoItems}
              routeOrders={transport?.routeOrders || []}
            />
          ) : null}
        />
      </div>

      {/* d. Koszty transportu — permission-gated exactly as before */}
      <TransportCostsSection
        transport={transport}
        canEditTransportCosts={canEditTransportCosts}
        showRateFields
        multiOrder={multiOrder}
        hasMixedQuantityUnits={hasMixedQuantityUnits}
        excludeManual={false}
        allocationPreview={allocationPreview}
        previewColumns={previewColumns}
        currency={currency}
        cargoLineCount={transportCostAllocation.items.length}
        warnings={transportCostAllocation.warnings}
        renderAllocationWarning={renderAllocationWarning}
        ldmFallbackMetric={fallbackMetric}
        missingDimensionOrders={missingDimensionOrders}
        onCostAllocationMethodTouched={markCostAllocationMethodTouched}
        onAllocationMethodTouched={markAllocationMethodTouched}
      />

      {/* e. Pojazd i kierowca + Uwagi — collapsed by default (ghost) */}
      <TransportVehicleNotesSection transport={transport} isEditMode={isEditMode} />

      {/* Sticky live cost summary — permission-gated by canViewTransportCosts. */}
      <TransportStickySummary
        canViewTransportCosts={canViewTransportCosts}
        currency={currency}
        formatCurrencyAmount={formatCurrencyAmount}
        totalGoodsValue={transportCostAllocation.summary.totalGoodsValue}
        transportCostForAllocation={transportCostForAllocation}
        totalLandedCost={transportCostAllocation.summary.totalLandedCost}
        multiOrder={multiOrder}
        selectedOrderCount={selectedOrders.length}
        submitButtons={stickySubmitButtons}
        missingRequiredFields={missingRequiredFields}
        onRequiredFieldClick={focusRequiredField}
        measuredHeight={stickySummaryHeight}
        onHeightChange={updateStickySummaryHeight}
      />
    </Form>
  );
};

const SharedTransportForm: React.FC<SharedTransportFormProps> = (props) => (
  <TransportFormSectionsProvider>
    <SharedTransportFormInner {...props} />
  </TransportFormSectionsProvider>
);

export default SharedTransportForm;
